import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import clsx from 'clsx';
import Grid from '@mui/material/Grid';
import {Divider} from '@mui/material';
import {user} from '../../../store/reducers/user';
import Notes from '../../../util/notes';
import useOpenState from '../../../hooks/useOpenState';
import MessageBox from '../../../util/messageBox';
import Formatter from '../../../util/formatter';
import Typography from '@mui/material/Typography';
import {
    ctxPatientType,
    ctxRemoveOption,
    setRemoveOption,
} from '../../../store/reducers/patientContext';
import MainScreenBar from './components/mainScreenBar';
import {useMainScreenStyles} from './mainScreenStyles';
import {getConfig} from '../../../util/configUtil';
import MainScreenAlerts from './components/mainScreenAlerts';
import MainScreenCoding from './components/mainScreenCoding';
import MainScreenGeneralInfo from './components/mainScreenGeneralInfo';
import MainScreenAssessments from './components/mainScreenAssessments';
import MainScreenTaskButtons from './components/mainScreenTaskButtons';
import {PATIENT_CONTEXT_TYPE} from '../../../util/patientContextUtil';

const MainScreen = (props) => {
    const classes = useMainScreenStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // user
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Selectors
    const patientType = useSelector(ctxPatientType);
    const removeOption = useSelector(ctxRemoveOption);

    const containerRef = useRef(null);

    // Configs
    const ecdsDateFormat = getConfig('ecdsDateFormat');
    const ecdsTimeFormat = getConfig('ecdsTimeFormat');

    const [currentPatientInWard, setCurrentPatientInWard] = React.useState(null);

    // Other forms open states
    const [isDischargeMoveOpen, openDischargeMove, closeDischargeMove] = useOpenState(false, true);
    const removePtOption = React.useRef(null);

    const getContextHeight = () => {
        let offset = 80;
        const header = document.getElementById('agyle-header');
        const infoBar = document.getElementById('information-bar');
        const msBar = document.getElementById('action-bar-mainscreen');
        if (header) offset += header?.offsetHeight;
        if (infoBar) offset += infoBar?.offsetHeight + 40;
        offset += msBar?.offsetHeight || 36;
        return offset;
    };

    useEffect(() => {
        setCurrentPatientInWard(props.patient);
    }, [props.patient]);

    useEffect(() => {
        if (removeOption) {
            removePtOption.current = removeOption;
            openDischargeMove();
            dispatch(setRemoveOption(null));
        }
    }, [removeOption]);

    const handleDischargeMove = () => {
        const saveObject = [
            {
                name: 'studies.discharge',
                noteType: 'studies.discharge',
                note: removePtOption.current === 'remove' ? Notes.getDischargeRemove() : Notes.getDischargeMove(),
                value: props.patient?.studies?.discharge,
                validation: null,
                isReadyToAdmit: props.patient?.clinicalConclusion?.status === 'Admitted',
                strategy: 'UHD_MOVE',
            },
        ];

        if (removePtOption.current === 'remove' && _.isEmpty(saveObject[0].value)) {
            const date = moment();
            saveObject[0].value = {
                by: tester,
                departureDate: date.utc().format(ecdsDateFormat),
                departureTime: date.utc().format(ecdsTimeFormat),
                departureDateAndTime: date.toISOString(),
                dischargeDestination: null,
                dischargeStatus: null,
                dischargeFollowUp: null,
            };
        }

        removePtOption.current = null;
        if (props.handleDischargePatient) props.handleDischargePatient(saveObject);
    };

    return (
        <div style={{marginRight: 6, paddingLeft: 10}} ref={containerRef}>
            {currentPatientInWard &&
                <>
                    <MainScreenBar
                        patient={currentPatientInWard}
                        disableSave={props.disableSave}
                        onSave={props.modifyValues}
                        pastAttendances={props.pastAttendances}
                    />

                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        alignContent="flex-start"
                        className={clsx('scroll-normal-white', classes.mainScreenContainer)}
                        style={{height: `calc(100vh - ${getContextHeight()}px)`}}
                    >
                        <Grid item xs={12} style={{paddingTop: 0}}>
                            <MainScreenAlerts/>
                        </Grid>

                        <Grid item xs={12} style={{paddingTop: 0}}>
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                style={{fontSize: '1.2rem', paddingLeft: 10}}
                            >
                                {t('General Information')}
                            </Typography>

                            <Divider/>
                        </Grid>

                        <MainScreenGeneralInfo
                            patient={currentPatientInWard}
                            disableSave={props.disableSave}
                            container={containerRef.current}
                        />

                        {patientType !== PATIENT_CONTEXT_TYPE.REVIEW &&
                            <Grid item xs={12} sx={{mt: 1}}>
                                <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                    style={{fontSize: '1.2rem', paddingLeft: 10}}
                                >
                                    {t('Treatment & Investigations')}
                                </Typography>

                                <Divider/>
                            </Grid>
                        }

                        {/* Second buttons section */}
                        <Grid item xs={12} sx={{mb: .5}}>
                            <MainScreenCoding
                                patient={currentPatientInWard}
                                disableSave={props.disableSave}
                                modifyValues={props.modifyValues}
                            />
                        </Grid>

                        <MainScreenTaskButtons
                            patient={currentPatientInWard}
                            disableSave={props.disableSave}
                            container={containerRef.current}
                        />

                        <MainScreenAssessments
                            patient={currentPatientInWard}
                            disableSave={props.disableSave}
                            container={containerRef.current}
                        />

                        <MessageBox
                            title={t('Confirmation')}
                            haveAccept={{yes: t('Submit'), no: t('Close')}}
                            warning
                            onAccept={handleDischargeMove}
                            onClose={closeDischargeMove}
                            message={t('Are you sure you want to remove patient from ED?')}
                            open={isDischargeMoveOpen}
                        />
                    </Grid>
                </>
            }
        </div>
    );
};

export default MainScreen;